import React, { useState } from 'react';
import CommonModal from './Modal';

const ExcelUploadModal = ({ isOpen, onClose, onUpload, api }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [hasPreviewedCurrentFile, setHasPreviewedCurrentFile] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
        alert('Please select a valid Excel file (.xlsx, .xls) or CSV file (.csv)');
        return;
      }
      setFile(selectedFile);
      setPreviewData([]);
      setValidationErrors([]);
      setImportResult(null);
      setShowAllErrors(false);
      setHasPreviewedCurrentFile(false); // Reset preview status for new file
    }
  };

  const handlePreview = async () => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    setImportResult(null);
    setHasPreviewedCurrentFile(true); // Mark that we've attempted to preview this file

    const formData = new FormData();
    formData.append('excel_file', file, file.name);

    try {

      const response = await fetch('/api/v1/bulk_users/preview', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Show detailed server error
        const errorDetails = {
          status: response.status,
          message: responseData.error || `HTTP ${response.status}`,
          details: responseData.details || null,
          receivedParams: responseData.received_params || null,
          contentType: responseData.content_type || null
        };

        setImportResult({
          success: false,
          created: 0,
          failed: 0,
          errors: [errorDetails.message],
          message: 'File processing failed',
          serverError: errorDetails,
          isPreviewError: true // Mark this as a preview error
        });

        throw new Error(responseData.error || `HTTP ${response.status}`);
      }

      const data = responseData.data || [];
      const errors = responseData.validation_errors || [];

      setPreviewData(Array.isArray(data) ? data : []);
      setValidationErrors(Array.isArray(errors) ? errors : []);

      if (data.length === 0 && errors.length === 0) {
        setImportResult({
          success: false,
          created: 0,
          failed: 0,
          errors: ['No valid data found in the file'],
          message: 'File appears to be empty or has no valid data rows',
          isPreviewError: true
        });
      }

    } catch (error) {
      if (!importResult) { // Only set if not already set above
        let errorMessage = error.message || 'Failed to preview file';
        let suggestions = [];

        // Provide specific guidance for common issues
        if (errorMessage.includes('Zip end of central directory')) {
          suggestions = [
            'The Excel file appears to be corrupted or in an unsupported format',
            'Try opening the file in Excel and saving it as a new .xlsx file',
            'Convert the file to CSV format',
            'Use a different Excel file'
          ];
        } else if (errorMessage.includes('WPS Office')) {
          suggestions = [
            'Files created with WPS Office may have compatibility issues',
            'Open the file in Microsoft Excel and save as .xlsx',
            'Convert to CSV format'
          ];
        } else if (errorMessage.includes('Network')) {
          suggestions = [
            'Check your internet connection',
            'Try again in a few moments',
            'Contact system administrator if problem persists'
          ];
        }

        setImportResult({
          success: false,
          created: 0,
          failed: 0,
          errors: [errorMessage],
          suggestions: suggestions,
          message: 'File preview failed',
          isPreviewError: true
        });
      }

      setPreviewData([]);
      setValidationErrors([]);
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!previewData || !Array.isArray(previewData) || previewData.length === 0) {
      alert('No data to import. Please preview the file first.');
      return;
    }

    setUploading(true);
    setImportResult(null);

    try {
      const response = await api.post('/bulk_users/import', {
        personnel: previewData
      });

      let responseData = response;
      if (response.data) {
        responseData = response.data;
      }

      const createdCount = responseData.created || 0;
      const failedCount = responseData.failed || 0;
      const errors = responseData.errors || [];
      const message = responseData.message || '';

      const result = {
        success: failedCount === 0,
        created: createdCount,
        failed: failedCount,
        errors: errors,
        message: message,
        totalProcessed: createdCount + failedCount,
        isImportResult: true // Mark this as an import result
      };

      setImportResult(result);

      if (result.success) {
        // All records imported successfully
        setTimeout(() => {
          handleClose(); // Close modal after showing success
        }, 3000); // Give user time to see the success message

        // Call onUpload if provided (for any parent component updates)
        if (onUpload) {
          onUpload();
        }
      }
      // If there are errors, keep modal open for user to try again

    } catch (error) {
      let errorMessage = 'Import failed';
      let errors = [];
      let serverError = null;

      if (error.response?.data) {
        const errorData = error.response.data;
        errorMessage = errorData.error || errorData.message || 'Import failed';
        errors = errorData.errors || [];

        serverError = {
          status: error.response.status,
          statusText: error.response.statusText,
          data: errorData
        };
      } else if (error.message) {
        errorMessage = error.message;
        errors = [error.message];
      }

      setImportResult({
        success: false,
        created: 0,
        failed: previewData.length,
        errors: errors.length > 0 ? errors : [errorMessage],
        message: errorMessage,
        serverError: serverError,
        isImportResult: true
      });

    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setImportResult(null);
    setShowAllErrors(false);
    setUploading(false);
    setHasPreviewedCurrentFile(false);
    onClose();
  };

  const handleTryAgain = () => {
    setFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setImportResult(null);
    setShowAllErrors(false);
    setHasPreviewedCurrentFile(false);
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
    console.log('Try again clicked - all states reset');
  };

  const downloadTemplate = () => {
    const headers = [
      'email', 'full_name', 'phone', 'bank_account', 'gender', 'marital_status', 'birth_date',
      'blood_type', 'salary_usd', 'allowance_usd', 'address', 'educational_level',
      'emergency_contact', 'emergency_contact_phone', 'emergency_contact_relation',
      'responsible_person_name', 'responsible_person_phone', 'training', 'rank',
      'army_equipment', 'army_type', 'army_department', 'sub_department',
      'organization', 'title', 'station', 'district', 'region', 'mother_name',
      'place_of_birth', 'status', 'remarks', 'work_place'
    ];

    const sampleRow = [
      'john.doe@example.com', 'John Doe', '+252123456789', '1009876543', 'Male', 'Single', '1990-01-01',
      'O+', '500', '100', '123 Main St', 'Bachelor', 'Jane Doe', '+252987654321', 'Sister',
      'Bob Smith', '+252555666777', 'Basic Training', 'Private', 'Rifle, Uniform', 'Puntland Military Force',
      'Infantry', '1st Battalion', 'Army HQ', 'Commander', 'Garowe', 'Nugaal', 'Puntland',
      'Mary Doe', 'Garowe', 'Active', 'Good soldier', 'Field Operations'
    ];

    const csvContent = headers.join(',') + '\n' + sampleRow.map(cell => `"${cell}"`).join(',');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'personnel_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk Personnel Import"
      size="lg"
    >
      <div className="mb_16">
        <button className="btn btn-info" onClick={downloadTemplate}>
          Download Template
        </button>
        <small className="text-muted d-block mt-1">
          Download the CSV template. For best compatibility, use this CSV template or save Excel files in standard .xlsx format.
        </small>
      </div>

      <div className="alert alert-info mb_16">
        <h6>File Format Guidelines:</h6>
        <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
          <li><strong>Recommended:</strong> CSV format (.csv) - Most reliable</li>
          <li><strong>Excel:</strong> Standard .xlsx format (avoid WPS Office formats)</li>
          <li><strong>Required columns:</strong> full_name, phone</li>
          <li><strong>First row:</strong> Must contain column headers</li>
        </ul>
      </div>

      {/* Detailed Import Result Display */}
      {importResult && (
        <div className={`alert ${importResult.success ? 'alert-success' : 'alert-danger'} mb_16`}>
          <h6>
            {importResult.success ? '✅ Import Successful!' :
             importResult.isPreviewError ? '❌ Preview Failed' : '❌ Import Failed'}
          </h6>

          <div style={{ marginBottom: '10px' }}>
            <strong>Summary:</strong>
            <ul style={{ marginBottom: 0, marginTop: '5px' }}>
              <li>Successfully imported: <strong>{importResult.created}</strong> records</li>
              {importResult.failed > 0 && (
                <li>Failed: <strong>{importResult.failed}</strong> records</li>
              )}
              {importResult.totalProcessed && (
                <li>Total processed: <strong>{importResult.totalProcessed}</strong> records</li>
              )}
            </ul>
          </div>

          {importResult.message && (
            <div style={{ marginBottom: '10px' }}>
              <strong>Message:</strong> {importResult.message}
            </div>
          )}

          {importResult.errors && importResult.errors.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <strong>Detailed Errors:</strong>
              <div style={{
                maxHeight: showAllErrors ? 'none' : '200px',
                overflowY: showAllErrors ? 'visible' : 'auto',
                marginTop: '5px',
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <ul style={{ marginBottom: 0 }}>
                  {(showAllErrors ? importResult.errors : importResult.errors.slice(0, 5)).map((error, index) => (
                    <li key={index} style={{ marginBottom: '5px', fontFamily: 'monospace', fontSize: '12px' }}>
                      {error}
                    </li>
                  ))}
                </ul>

                {importResult.errors.length > 5 && !showAllErrors && (
                  <div style={{ marginTop: '10px' }}>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowAllErrors(true)}
                    >
                      Show All {importResult.errors.length} Errors
                    </button>
                  </div>
                )}

                {showAllErrors && importResult.errors.length > 5 && (
                  <div style={{ marginTop: '10px' }}>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowAllErrors(false)}
                    >
                      Show Less
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {importResult.suggestions && importResult.suggestions.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <strong>Suggestions:</strong>
              <ul style={{ marginTop: '5px', marginBottom: 0 }}>
                {importResult.suggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          {importResult.serverError && (
            <details style={{ marginTop: '10px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                Technical Details (Click to expand)
              </summary>
              <pre style={{
                backgroundColor: '#f1f1f1',
                padding: '10px',
                marginTop: '5px',
                fontSize: '11px',
                overflow: 'auto',
                maxHeight: '150px'
              }}>
                {JSON.stringify(importResult.serverError, null, 2)}
              </pre>
            </details>
          )}

          {!importResult.success && (
            <div style={{ marginTop: '15px' }}>
              <button className="btn btn-warning" onClick={handleTryAgain}>
                Try Another File
              </button>
            </div>
          )}

          {importResult.success && (
            <div style={{ marginTop: '10px', color: 'green', fontWeight: 'bold' }}>
              🎉 All records imported successfully! Modal will close automatically in 3 seconds...
            </div>
          )}
        </div>
      )}

      <div className="formInpt mb_16">
        <label className="mb_8">Select Excel or CSV File</label>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
        />
        {file && (
          <small className="text-muted d-block mt-1">
            Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
          </small>
        )}
      </div>

      {/* Simplified Preview Button Logic */}
      {file && !uploading && !hasPreviewedCurrentFile && (
        <div className="mb_16">
          <button
            className="btn btn-secondary"
            onClick={handlePreview}
            disabled={uploading}
          >
            {uploading ? 'Processing...' : 'Preview Data'}
          </button>
        </div>
      )}

      {/* Show preview button again if there was a preview error */}
      {file && !uploading && hasPreviewedCurrentFile && importResult?.isPreviewError && (
        <div className="mb_16">
          <button
            className="btn btn-secondary"
            onClick={handlePreview}
            disabled={uploading}
          >
            {uploading ? 'Processing...' : 'Try Preview Again'}
          </button>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="alert alert-warning mb_16">
          <h6>⚠️ Validation Errors ({validationErrors.length}):</h6>
          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
            <ul style={{ marginBottom: 0 }}>
              {validationErrors.slice(0, 20).map((error, index) => (
                <li key={index} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {error}
                </li>
              ))}
              {validationErrors.length > 20 && (
                <li style={{ fontWeight: 'bold' }}>
                  ... and {validationErrors.length - 20} more errors
                </li>
              )}
            </ul>
          </div>
          <small className="text-muted d-block mt-1">
            <strong>Note:</strong> Records with validation errors will be skipped during import.
          </small>
        </div>
      )}

      {Array.isArray(previewData) && previewData.length > 0 && !importResult?.isImportResult && (
        <div className="mt_16">
          <h6>📋 Preview ({previewData.length} records)</h6>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <table className="baseTable">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Full Name</th>
                  <th>Phone</th>
                  <th>Army Type</th>
                  <th>Department</th>
                  <th>Rank</th>
                </tr>
              </thead>
              <tbody>
                {previewData.slice(0, 10).map((row, index) => (
                  <tr key={index}>
                    <td>{row.email || ''}</td>
                    <td>{row.full_name || ''}</td>
                    <td>{row.phone || ''}</td>
                    <td>{row.army_type || ''}</td>
                    <td>{row.army_department || ''}</td>
                    <td>{row.rank || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {previewData.length > 10 && (
              <p className="text-muted">... and {previewData.length - 10} more records</p>
            )}
          </div>

          <div className="mt_16">
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? 'Importing...' : `Import ${previewData.length} Records`}
            </button>
            {validationErrors.length > 0 && (
              <small className="text-warning d-block mt-1">
                <strong>Note:</strong> Records with validation errors will be skipped during import.
              </small>
            )}
          </div>
        </div>
      )}
    </CommonModal>
  );
};

export default ExcelUploadModal;
